import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { dictionaries, supportedLocales } from "./translations";

const I18nContext = createContext({
  locale: "pt-BR",
  t: dictionaries["pt-BR"],
  setLocale: () => {},
  toggleLocale: () => {},
  currency: "BRL",
});

const STORAGE_KEY = "gm.locale";

function detectInitialLocale() {
  if (typeof window === "undefined") return "pt-BR";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && supportedLocales.includes(stored)) return stored;
  // querystring override
  const qs = new URLSearchParams(window.location.search).get("lang");
  if (qs && supportedLocales.includes(qs)) return qs;
  // navigator
  const nav = (navigator.language || "pt-BR").toLowerCase();
  if (nav.startsWith("pt")) return "pt-BR";
  return "en";
}

function resolveCurrency(locale) {
  // Brazil → BRL, everyone else → USD
  return locale === "pt-BR" ? "BRL" : "USD";
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => detectInitialLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = (next) => {
    if (supportedLocales.includes(next)) setLocaleState(next);
  };

  const toggleLocale = () => setLocaleState((l) => (l === "pt-BR" ? "en" : "pt-BR"));

  const value = useMemo(
    () => ({
      locale,
      t: dictionaries[locale],
      setLocale,
      toggleLocale,
      currency: resolveCurrency(locale),
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
